import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../hooks";
import FilmCard from "../../components/FilmCard/FilmCard";
import { useNavigate } from "react-router";
import {
  useGetPopularFilmsQuery,
  useGetFeedQuery,
  useGetWatchPartiesQuery,
  useSearchFilmsQuery,
  useSearchUsersQuery,
} from "../../services/api";

const Home: React.FC = () => {
  const navigate = useNavigate();
  const authUser = useAppSelector((s) => s.auth.user);

  // UI State
  const [activeTab, setActiveTab] = useState<"activity" | "discover">("discover");
  const [visibleCount, setVisibleCount] = useState(15);
  const [selectedGenre, setSelectedGenre] = useState<string>("All Genres");
  const [filter, setFilter] = useState<"all" | "movie" | "tvSeries">("all");

  // Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // RTK Query Hooks
  const { data: films = [], isLoading: loading } = useGetPopularFilmsQuery();
  const { data: feedData, isLoading: activityLoading } = useGetFeedQuery(authUser?.id || "", {
    skip: !authUser,
  });
  const { data: activeParties = [] } = useGetWatchPartiesQuery(
    { filter: "upcoming", limit: 5, userId: authUser?.id },
    { skip: !authUser }
  );

  // Search Autocomplete
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: autoFilmsData = [] } = useSearchFilmsQuery(
    { query: debouncedSearchTerm, limit: 5 },
    { skip: debouncedSearchTerm.length < 2 }
  );
  const { data: autoUsersData = [] } = useSearchUsersQuery(
    { query: debouncedSearchTerm, limit: 5 },
    { skip: debouncedSearchTerm.length < 2 }
  );

  const searchResults = React.useMemo(() => {
    if (debouncedSearchTerm.length < 2) return [];
    return [
      ...(autoUsersData || []).map((u: any) => ({ ...u, type: "user" })),
      ...(autoFilmsData || []).map((f: any) => ({ ...f, type: f.type || "movie" })),
    ];
  }, [autoUsersData, autoFilmsData, debouncedSearchTerm]);

  useEffect(() => {
    if (searchResults.length > 0 && debouncedSearchTerm.length >= 2) {
      setShowAutocomplete(true);
    } else {
      setShowAutocomplete(false);
    }
  }, [searchResults, debouncedSearchTerm]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchResults]);

  // Derived Data
  const followingActivity = feedData?.activities || [];
  const suggestedUsers = feedData?.suggestions || [];

  // Set default tab based on auth
  useEffect(() => {
    if (authUser) {
      setActiveTab("activity");
    }
  }, [authUser]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.currentTarget;
    target.onerror = null; // Prevent infinite loop
    target.src = "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar";
  };

  const handleAutocompleteClick = (item: any) => {
    if (item.type === "user") {
      navigate(`/profile/${item.username}`);
    } else {
      window.location.href = `/chat/${item.imdb_id}`;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "currently_watching":
        return "👁️";
      case "watched":
        return "✓";
      case "favorite":
        return "♥";
      case "will_watch":
        return "📌";
      default:
        return "📺";
    }
  };

  const getActivityText = (type: string) => {
    switch (type) {
      case "currently_watching":
        return "is watching";
      case "watched":
        return "watched";
      case "favorite":
        return "favorited";
      case "will_watch":
        return "wants to watch";
      default:
        return "interacted with";
    }
  };

  const availableGenres = React.useMemo(() => {
    const genres = new Set<string>();
    films.forEach((f: any) => {
      if (f.genres) {
        let filmGenres: string[] = [];
        if (Array.isArray(f.genres)) {
          filmGenres = f.genres;
        } else if (typeof f.genres === "string") {
          try {
            filmGenres = JSON.parse(f.genres);
          } catch {
            // ignore
          }
        }
        filmGenres.forEach((g) => genres.add(g));
      }
    });
    return ["All Genres", ...Array.from(genres).sort()];
  }, [films]);

  const filtered = films.filter((f: any) => {
    const matchesType = filter === "all" ? true : f.type === filter;
    const matchesGenre =
      selectedGenre === "All Genres"
        ? true
        : Array.isArray(f.genres)
        ? f.genres.includes(selectedGenre)
        : typeof f.genres === "string" && f.genres.includes(selectedGenre);
    return matchesType && matchesGenre;
  });
  const visibleFilms = filtered.slice(0, visibleCount);

  if (loading) {
    return <div className="flex-1 flex items-center justify-center bg-gray-950 text-white">Loading...</div>;
  }

  return (
    <div className="flex-1 bg-gray-950 text-white min-h-screen">
      {/* Mobile Tabs */}
      <div className="lg:hidden sticky top-0 z-20 bg-gray-900/95 backdrop-blur border-b border-gray-800 flex">
        <button
          onClick={() => setActiveTab("activity")}
          className={`flex-1 py-4 font-semibold text-sm transition-colors ${
            activeTab === "activity" ? "text-red-500 border-b-2 border-red-500" : "text-gray-400"
          }`}
        >
          Activity Feed
        </button>
        <button
          onClick={() => setActiveTab("discover")}
          className={`flex-1 py-4 font-semibold text-sm transition-colors ${
            activeTab === "discover" ? "text-red-500 border-b-2 border-red-500" : "text-gray-400"
          }`}
        >
          Discover
        </button>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT SIDEBAR - Navigation & Search (Hidden on Mobile if in Activity tab, visible in Discover) */}
          <div className={`lg:col-span-2 ${activeTab === "activity" ? "hidden lg:block" : "block"}`}>
            <div className="sticky top-24 space-y-6">
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowAutocomplete(true)}
                  onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setSelectedIndex((prev) => Math.min(prev + 1, searchResults.length - 1));
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setSelectedIndex((prev) => Math.max(prev - 1, -1));
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      if (selectedIndex >= 0 && searchResults[selectedIndex]) {
                        handleAutocompleteClick(searchResults[selectedIndex]);
                      } else {
                        navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                        setShowAutocomplete(false);
                      }
                    }
                  }}
                  placeholder="Search..."
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-sm focus:border-red-500 focus:outline-none"
                />
                {/* Autocomplete Dropdown */}
                {showAutocomplete && searchResults.length > 0 && (
                  <div className="absolute top-full mt-2 w-full bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                    {searchResults.map((item: any, index: number) => (
                      <button
                        key={item.id}
                        onClick={() => handleAutocompleteClick(item)}
                        className={`w-full flex items-center gap-3 p-3 hover:bg-gray-800 text-left border-b border-gray-800 last:border-0 ${
                          index === selectedIndex ? "bg-gray-700" : ""
                        }`}
                      >
                        <img
                          src={
                            item.type === "user"
                              ? item.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"
                              : item.thumbnails
                              ? JSON.parse(item.thumbnails)[0]?.url
                              : item.primary_image
                          }
                          className={`object-cover ${item.type === "user" ? "w-8 h-8 rounded-full" : "w-8 h-10 rounded"}`}
                          alt={item.type === "user" ? item.username : item.original_title}
                          onError={handleImageError}
                        />
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">
                            {item.type === "user" ? item.username : item.original_title}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">{item.type === "user" ? "User" : item.type}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Filters */}
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Browse</h3>
                <button
                  onClick={() => setActiveTab("activity")}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "activity" ? "bg-red-600 text-white" : "text-gray-400 hover:bg-gray-900"
                  }`}
                >
                  Activity Feed
                </button>
                <button
                  onClick={() => {
                    setFilter("all");
                    setActiveTab("discover");
                  }}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "discover" && filter === "all" ? "bg-red-600 text-white" : "text-gray-400 hover:bg-gray-900"
                  }`}
                >
                  All Content
                </button>
                <button
                  onClick={() => {
                    setFilter("movie");
                    setActiveTab("discover");
                  }}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "discover" && filter === "movie"
                      ? "bg-red-600 text-white"
                      : "text-gray-400 hover:bg-gray-900"
                  }`}
                >
                  Movies
                </button>
                <button
                  onClick={() => {
                    setFilter("tvSeries");
                    setActiveTab("discover");
                  }}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "discover" && filter === "tvSeries"
                      ? "bg-red-600 text-white"
                      : "text-gray-400 hover:bg-gray-900"
                  }`}
                >
                  TV Shows
                </button>
                <button
                  onClick={() => navigate("/watch-parties")}
                  className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-purple-400 hover:bg-purple-900/20"
                >
                  🎉 Watch Parties
                </button>
              </div>

              {/* Genre Filter */}
              {activeTab === "discover" && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Genres</h3>
                  <div className="relative">
                    <select
                      value={selectedGenre}
                      onChange={(e) => {
                        setSelectedGenre(e.target.value);
                        setVisibleCount(15);
                      }}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-sm focus:border-red-500 focus:outline-none appearance-none text-gray-300"
                    >
                      {availableGenres.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CENTER - Activity Feed (Visible on Activity Tab) */}
          <div className="lg:col-span-8">
            {activeTab === "discover" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {visibleFilms.map((film: any) => (
                  <FilmCard key={film.id} film={film} />
                ))}
                {visibleCount < filtered.length && (
                  <div className="col-span-full flex justify-center py-8">
                    <button
                      onClick={() => setVisibleCount((c) => c + 10)}
                      className="px-6 py-2 bg-gray-800 hover:bg-gray-700 rounded-full text-sm font-medium transition-colors"
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {!authUser ? (
                  <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
                    <h2 className="text-xl font-bold mb-2">Join the Community</h2>
                    <p className="text-gray-400 mb-6">Sign in to see what your friends are watching!</p>
                    <button onClick={() => navigate("/login")} className="px-6 py-2 bg-red-600 rounded-lg font-medium">
                      Sign In
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Feed Header */}
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold">Activity Feed</h2>
                      {activityLoading && (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                      )}
                    </div>

                    {/* Empty State / Suggestions / Trending Fallback */}
                    {followingActivity.length === 0 && !activityLoading && (
                      <div className="space-y-8">
                        {/* Welcome / No Activity Message */}
                        <div className="bg-gray-900/50 rounded-xl p-6 text-center border border-gray-800">
                          <p className="text-gray-400 mb-4">
                            {authUser
                              ? "No recent activity from your friends. Start following people to see what they're watching!"
                              : "Sign in to follow your friends and see their activity!"}
                          </p>

                          {/* Suggested Users */}
                          {suggestedUsers.length > 0 && (
                            <div className="text-left mt-6">
                              <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Suggested People to Follow</h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {suggestedUsers.map((user: any) => (
                                  <div
                                    key={user.id}
                                    className="flex items-center justify-between bg-gray-900 p-3 rounded-lg border border-gray-800"
                                  >
                                    <div
                                      className="flex items-center gap-3 cursor-pointer"
                                      onClick={() => navigate(`/profile/${user.username}`)}
                                    >
                                      <img
                                        src={user.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"}
                                        className="w-10 h-10 rounded-full"
                                        alt={user.username || "User"}
                                        onError={handleImageError}
                                      />
                                      <span className="font-medium text-sm">{user.username}</span>
                                    </div>
                                    <button
                                      onClick={() => navigate(`/profile/${user.username}`)}
                                      className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-full transition-colors"
                                    >
                                      View
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Activity Items */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                      {followingActivity.map((activity: any) => (
                        <div
                          key={activity.id}
                          className="bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors"
                        >
                          <div className="flex items-start gap-4">
                            <button onClick={() => navigate(`/profile/${activity.user.username}`)} className="shrink-0">
                              <img
                                src={activity.user.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"}
                                className="w-12 h-12 rounded-full border-2 border-gray-800"
                                alt={activity.user.username || "User"}
                                onError={handleImageError}
                              />
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-baseline gap-1 text-sm mb-1">
                                <button
                                  onClick={() => navigate(`/profile/${activity.user.username}`)}
                                  className="font-bold hover:underline"
                                >
                                  {activity.user.username}
                                </button>
                                <span className="text-gray-400">{getActivityText(activity.activity_type)}</span>
                                <button
                                  onClick={() => navigate(`/chat/${activity.film.imdb_id}`)}
                                  className="font-bold text-red-400 hover:underline truncate max-w-[200px]"
                                >
                                  {activity.film.original_title}
                                </button>
                                <span className="text-lg ml-1">{getActivityIcon(activity.activity_type)}</span>
                              </div>
                              <p className="text-xs text-gray-500 mb-3">
                                {new Date(activity.created_at).toLocaleDateString()} at{" "}
                                {new Date(activity.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </p>

                              {/* Film Snippet */}
                              <div
                                onClick={() => navigate(`/chat/${activity.film.imdb_id}`)}
                                className="flex gap-3 bg-gray-950/50 p-2 rounded-lg cursor-pointer hover:bg-gray-950 transition-colors"
                              >
                                <img src={activity.film.primary_image} className="w-12 h-16 object-cover rounded" alt="" />
                                <div className="py-1">
                                  <div className="font-medium text-sm line-clamp-1">{activity.film.original_title}</div>
                                  <div className="text-xs text-gray-500">
                                    {activity.film.start_year} • {activity.film.type}
                                  </div>
                                  <div className="text-xs text-yellow-500 mt-1">
                                    ⭐ {Number(activity.film.average_rating).toFixed(1)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Always Show Trending/Suggested Films at Bottom */}
                    <div className="pt-8 border-t border-gray-800">
                      <h3 className="text-lg font-bold text-white mb-4">
                        {followingActivity.length > 0 ? "More to Watch" : "Trending on Noleyo"}
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {filtered.slice(0, 8).map((film: any) => (
                          <FilmCard key={film.id} film={film} />
                        ))}
                      </div>
                      <button
                        onClick={() => {
                          setActiveTab("discover");
                          setFilter("all");
                        }}
                        className="w-full mt-6 py-3 bg-gray-900 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-400 hover:text-white transition-colors"
                      >
                        Discover More
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR - Trending & Parties (Hidden on Mobile) */}
          <div className="hidden lg:block lg:col-span-2 space-y-6">
            <div className="sticky top-24">
              {/* Active Parties */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                  <span className="text-green-500">●</span> Live Parties
                </h3>
                {activeParties.length === 0 ? (
                  <p className="text-sm text-gray-500">No upcoming parties.</p>
                ) : (
                  <div className="space-y-3">
                    {activeParties.map((party: any) => (
                      <div key={party.id} className="group cursor-pointer" onClick={() => navigate(`/watch-party/${party.id}`)}>
                        <div className="text-sm font-medium group-hover:text-red-400 transition-colors truncate">
                          {party.title}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(party.scheduled_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => navigate("/watch-parties")}
                  className="w-full mt-4 text-xs font-bold text-gray-500 hover:text-white uppercase tracking-wider"
                >
                  View All
                </button>
              </div>

              {/* Trending Mini List */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <h3 className="font-bold text-white mb-4">Trending Now</h3>
                <div className="space-y-4">
                  {filtered.slice(0, 5).map((film: any, i: number) => (
                    <div key={film.id} className="flex gap-3 cursor-pointer group" onClick={() => navigate(`/chat/${film.imdb_id}`)}>
                      <div className="text-lg font-bold text-gray-700 w-4">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium group-hover:text-red-400 transition-colors truncate">
                          {film.original_title}
                        </div>
                        <div className="text-xs text-gray-500">{film.start_year}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
